import { assign, createMachine } from 'xstate';
import { escalate } from 'xstate/lib/actions';
import { CredentialsEntity } from '../../models/credentials';
import { CredentialsService, credentialsService } from '../services/credentialsService';

type CredentialsPollerContext = {
  credentials: CredentialsEntity;
  pollCount: number;
};

export type CredentialsPollerMachineParams = {
  credentials: CredentialsEntity;
  pollCountMax?: number;
  service?: CredentialsService;
};

type CredentialsServices = {
  getCredentials: {
    data: CredentialsEntity;
  };
};

export const createCredentialsPollerMachine = ({
  credentials,
  pollCountMax,
  service = credentialsService,
}: CredentialsPollerMachineParams) =>
  createMachine(
    {
      predictableActionArguments: true,
      schema: {
        context: {} as CredentialsPollerContext,
        services: {} as CredentialsServices,
      },
      tsTypes: {} as import('./credentialsPollerMachine.typegen').Typegen0,
      id: 'credentials-poller-machine',
      initial: 'processGetCredentials',
      context: {
        credentials,
        pollCount: 0,
      },
      states: {
        processGetCredentials: {
          invoke: {
            src: 'getCredentials',
            onDone: {
              target: 'decisionPollingDone',
              actions: ['setCredentials', 'incrementPollCount'],
            },
            onError: {
              target: 'error',
              actions: ['throwError'],
            },
          },
        },
        decisionPollingDone: {
          always: {
            target: 'processDone',
            cond: 'isPollingDone',
          },
          after: {
            1000: { target: 'processGetCredentials' },
          },
        },
        processDone: {
          type: 'final',
          data: (ctx) => ctx.credentials,
        },
        error: {
          type: 'final',
        },
      },
    },
    {
      services: {
        getCredentials: () => service.getCredentials(credentials.id),
      },
      guards: {
        isPollingDone: (ctx) =>
          ctx.credentials.statusUpdated !== credentials.statusUpdated ||
          (pollCountMax ? ctx.pollCount >= pollCountMax : false),
      },
      actions: {
        setCredentials: assign({ credentials: (_, event) => event.data }),
        incrementPollCount: assign({ pollCount: (ctx) => ctx.pollCount + 1 }),
        throwError: escalate((_, event) => event.data),
      },
    }
  );
